import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { SUPPORT_MESSAGE_MAX, SUPPORT_REQUEST_STATUS_LABEL, type SupportRequestDocumentView, type SupportRequestView, type SupportSummary } from '@pqrs/contracts';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Icono } from '@xiltrion/ui/icono';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import { SelectorMultiple } from '@xiltrion/ui/bloques/selectores';
import { Spinner } from '@xiltrion/ui/spinner';
import { Textarea } from '@xiltrion/ui/textarea';
import { RichTextEditor, RichTextView } from '@/components/RichTextEditor';
import { isRichEmpty, richPlainText } from '@/components/richText';
import { Card, EmptyState, ErrorState, Field, LoadingState, fmtDate } from '@/components/ui';
import { CaseFilesDrop } from '@/features/cases/CaseFilesDrop';
import { errorMessage, fmtBytes, notify } from '@/features/cases/lib';
import { useAnswerSupportRequest, useCancelSupportRequest, useCaseSupport, useCreateSupportRequest, useSupportUnits } from './api';

const STATUS_BADGE: Record<SupportRequestView['status'], { variant: 'warning-light' | 'success-light' | 'secondary'; icon: 'mdi:clock-outline' | 'mdi:check-circle-outline' | 'mdi:cancel' }> = {
  PENDING: { variant: 'warning-light', icon: 'mdi:clock-outline' },
  ANSWERED: { variant: 'success-light', icon: 'mdi:check-circle-outline' },
  CANCELLED: { variant: 'secondary', icon: 'mdi:cancel' },
};

export function SupportStatusBadge({ status }: { status: SupportRequestView['status'] }) {
  const b = STATUS_BADGE[status];
  return <Badge variant={b.variant}><Icono nombre={b.icon} aria-hidden /> {SUPPORT_REQUEST_STATUS_LABEL[status]}</Badge>;
}

/** Qué áreas ya respondieron y cuáles siguen pendientes, de un vistazo. */
export function SupportSummaryBar({ summary }: { summary: SupportSummary }) {
  if (!summary.units.length) return null;
  const answered = summary.units.filter((u) => u.status === 'ANSWERED').length;
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm"><span className="font-medium tnum">{answered} de {summary.units.length}</span> {summary.units.length === 1 ? 'área respondió' : 'áreas respondieron'}{summary.pending > 0 && <span className="text-muted-foreground"> · <span className="tnum">{summary.pending}</span> {summary.pending === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'}</span>}</div>
      <ul className="list-none m-0 p-0 flex flex-wrap gap-1.5" aria-label="Estado por área">
        {summary.units.map((u) => <li key={u.unitId}><Badge variant={u.status === 'ANSWERED' ? 'success-light' : 'warning-light'}><Icono nombre={u.status === 'ANSWERED' ? 'mdi:check' : 'mdi:clock-outline'} aria-hidden /> {u.name}<span className="sr-only">: {u.status === 'ANSWERED' ? 'respondió' : 'pendiente'}</span></Badge></li>)}
      </ul>
    </div>
  );
}

function FileList({ docs, label }: { docs: SupportRequestDocumentView[]; label: string }) {
  if (!docs.length) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <ul className="list-none m-0 p-0 flex flex-col gap-1 text-sm">{docs.map((d) => (
        <li key={d.id} className="flex items-center gap-2 min-w-0"><Icono nombre="mdi:paperclip" aria-hidden className="text-muted-foreground shrink-0" /><a href={`/api/v1/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="truncate">{d.fileName}</a><span className="text-xs text-muted-foreground tnum shrink-0">{fmtBytes(d.sizeBytes)}</span></li>
      ))}</ul>
    </div>
  );
}

/**
 * Una solicitud de apoyo: a qué área, quién la pidió y cuándo, el mensaje y sus archivos; la respuesta (quién, cuándo,
 * texto y archivos) o, si está pendiente y la persona responde en esa área, el formulario para responderla.
 */
export function SupportRequestItem({ r, caseId, defaultOpen = false }: { r: SupportRequestView; caseId: string; defaultOpen?: boolean }) {
  const [answering, setAnswering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const requestDocs = r.documents.filter((d) => d.role === 'REQUEST');
  const answerDocs = r.documents.filter((d) => d.role === 'ANSWER');
  return (
    <li className={`border rounded-lg p-3 text-sm flex flex-col gap-3 ${r.status === 'ANSWERED' ? 'border-success/30' : r.status === 'PENDING' ? 'border-warning/40' : 'border-border opacity-80'}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-medium inline-flex items-center gap-1.5"><Icono nombre="mdi:office-building-outline" aria-hidden /> {r.targetUnit.name}</div>
          <div className="text-xs text-muted-foreground">Pedida por {r.requestedBy.name} · {fmtDate(r.createdAt)}</div>
        </div>
        <SupportStatusBadge status={r.status} />
      </div>
      <Plegable titulo={<span className="text-xs">Mensaje enviado</span>} defaultAbierto={defaultOpen || r.status === 'PENDING'}>
        <p className="whitespace-pre-wrap m-0">{r.message}</p>
        <div className="mt-2"><FileList docs={requestDocs} label="Archivos enviados" /></div>
      </Plegable>
      {r.status === 'ANSWERED' && (
        <div className="rounded-md bg-success/5 border border-success/20 p-3 flex flex-col gap-2">
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Icono nombre="mdi:reply-outline" aria-hidden /> Respondió {r.answeredBy?.name ?? '—'} · {fmtDate(r.answeredAt)}</div>
          <RichTextView value={r.answer ?? ''} className="bg-card" />
          <FileList docs={answerDocs} label="Archivos de la respuesta" />
        </div>
      )}
      {r.status === 'CANCELLED' && <div className="text-xs text-muted-foreground">Cancelada{r.cancelledBy ? ` por ${r.cancelledBy.name}` : ''} · {fmtDate(r.cancelledAt)}{r.cancelReason ? ` · ${r.cancelReason}` : ''}</div>}
      {r.status === 'PENDING' && (r.can.answer || r.can.cancel) && !answering && (
        <div className="flex flex-wrap gap-2 justify-end">
          {r.can.cancel && <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}><Icono nombre="mdi:close" /> Cancelar solicitud</Button>}
          {r.can.answer && <Button size="sm" onClick={() => setAnswering(true)}><Icono nombre="mdi:reply-outline" /> Responder</Button>}
        </div>
      )}
      {answering && <AnswerForm r={r} caseId={caseId} onDone={() => setAnswering(false)} />}
      {cancelling && <CancelDialog r={r} caseId={caseId} onHide={() => setCancelling(false)} />}
    </li>
  );
}

function AnswerForm({ r, caseId, onDone }: { r: SupportRequestView; caseId: string; onDone: () => void }) {
  const answer = useAnswerSupportRequest(caseId);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const id = `support-answer-${r.id}`;
  const length = richPlainText(text).length;
  const tooLong = length > SUPPORT_MESSAGE_MAX;
  const valid = !isRichEmpty(text) && length >= 3 && !tooLong;
  const submit = () => answer.mutate({ id: r.id, answer: text, files }, { onSuccess: () => { notify('success', 'Respuesta enviada', `${r.requestedBy.name} ya puede consultarla en el caso.`); onDone(); } });
  return (
    <div className="flex flex-col gap-3 border-t pt-3">
      <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>Tu respuesta es interna: la recibe quien pidió el apoyo para elaborar la respuesta oficial. No se envía al ciudadano.</AlertDescription></Alert>
      <Field label={`Respuesta de ${r.targetUnit.name}`} htmlFor={id} required hint={`${length} / ${SUPPORT_MESSAGE_MAX} caracteres`} error={tooLong ? 'La respuesta es demasiado larga' : undefined}>
        <RichTextEditor id={id} value={text} onChange={setText} minHeight={200} invalid={tooLong} describedBy={`${id}-hint`} placeholder="La información, el análisis o el concepto que pidieron. Puedes usar listas, negrita, subrayado y tablas." />
      </Field>
      <Field label="Archivos de soporte (opcional)"><CaseFilesDrop archivos={files} onArchivosChange={setFiles} deshabilitado={answer.isPending} aria-label="Archivos de soporte de la respuesta" /></Field>
      {answer.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(answer.error)}</AlertDescription></Alert>}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="ghost" onClick={onDone} disabled={answer.isPending}>Cancelar</Button>
        <Button disabled={!valid || answer.isPending} onClick={submit}>{answer.isPending ? <Spinner /> : <Icono nombre="mdi:send-outline" />} Enviar respuesta</Button>
      </div>
    </div>
  );
}

function CancelDialog({ r, caseId, onHide }: { r: SupportRequestView; caseId: string; onHide: () => void }) {
  const cancel = useCancelSupportRequest(caseId);
  const [reason, setReason] = useState('');
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onHide(); }}>
      <DialogContent className="sm:max-w-[min(480px,95vw)]">
        <DialogHeader><DialogTitle>Cancelar la solicitud a {r.targetUnit.name}</DialogTitle><DialogDescription>El área deja de verla como pendiente y recibe un aviso con el motivo. Queda en la historia del caso.</DialogDescription></DialogHeader>
        <Field label="Motivo" htmlFor={`support-cancel-${r.id}`} required><Textarea id={`support-cancel-${r.id}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} autoFocus placeholder="Por ejemplo: la información ya llegó por otro medio." /></Field>
        {cancel.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(cancel.error)}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={onHide}>Volver</Button>
          <Button variant="destructive" disabled={reason.trim().length < 3 || cancel.isPending} onClick={() => cancel.mutate({ id: r.id, reason: reason.trim() }, { onSuccess: () => { notify('success', 'Solicitud de apoyo cancelada'); onHide(); } })}>{cancel.isPending ? <Spinner /> : <Icono nombre="mdi:close" />} Cancelar solicitud</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Pedir apoyo a una o varias áreas: una solicitud independiente por área, con el mismo mensaje y archivos. */
function RequestDialog({ caseId, ownUnitId, onHide }: { caseId: string; ownUnitId: string | null; onHide: () => void }) {
  const create = useCreateSupportRequest(caseId);
  const units = useSupportUnits();
  // Un área sin nadie que responda (ni líder ni personas activas con «Responde») se ve, pero no se puede elegir.
  const options = useMemo(() => (units.data ?? []).map((u) => ({
    value: u.id, label: u.name, deshabilitada: !u.attended,
    descripcion: !u.attended ? 'Sin quién responda: no tiene líder ni personas con «Responde»' : u.id === ownUnitId ? 'Área del caso' : undefined,
  })), [units.data, ownUnitId]);
  const unattended = (units.data ?? []).filter((u) => !u.attended).length;
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const valid = unitIds.length > 0 && message.trim().length >= 10;
  const submit = () => create.mutate({ input: { unitIds, message: message.trim() }, files }, {
    onSuccess: (rows) => { notify('success', rows.length === 1 ? 'Solicitud de apoyo enviada' : `${rows.length} solicitudes de apoyo enviadas`, 'El área recibe un aviso. Verás su respuesta aquí.'); onHide(); },
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !create.isPending) onHide(); }}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader><DialogTitle>Pedir apoyo a otras áreas</DialogTitle><DialogDescription>Cuando la petición toca temas de otra dependencia, pídele la información, el análisis o el concepto que necesitas. Cada área recibe su propia solicitud.</DialogDescription></DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <Field label="Áreas" htmlFor="support-units" required hint={`Puedes elegir varias: cada una responde por separado.${unattended ? ` ${unattended === 1 ? 'Un área aparece deshabilitada' : `${unattended} áreas aparecen deshabilitadas`} porque no tiene quién responda; el administrador lo configura en el organigrama.` : ''}`}>
            <SelectorMultiple id="support-units" value={unitIds} onChange={setUnitIds} options={options} buscable placeholder={units.isLoading ? 'Cargando áreas…' : 'Elegir áreas…'} textoVacio="No hay áreas activas" className="w-full" />
          </Field>
          <Field label="Qué necesitas" htmlFor="support-message" required hint={`Explica qué información, análisis o respuesta esperas del área · ${message.trim().length} / ${SUPPORT_MESSAGE_MAX}`}>
            <Textarea id="support-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={6} maxLength={SUPPORT_MESSAGE_MAX} placeholder="Por ejemplo: ¿el predio del peticionario está incluido en el plan de obras de 2026? Necesitamos la fecha estimada y el acto que lo respalda." />
          </Field>
          <Field label="Archivos (opcional)"><CaseFilesDrop archivos={files} onArchivosChange={setFiles} deshabilitado={create.isPending} aria-label="Archivos para la solicitud de apoyo" /></Field>
          <Alert><Icono nombre="mdi:shield-account-outline" /><AlertDescription>Es una comunicación interna: no se envía al ciudadano. Quienes responden en el área podrán consultar el expediente para darte su respuesta.</AlertDescription></Alert>
          {create.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(create.error)}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onHide} disabled={create.isPending}>Cancelar</Button>
          <Button disabled={!valid || create.isPending} onClick={submit}>{create.isPending ? <Spinner /> : <Icono nombre="mdi:send-outline" />} {unitIds.length > 1 ? `Enviar ${unitIds.length} solicitudes` : 'Enviar solicitud'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Pestaña «Apoyos» del expediente: estado general, cada solicitud y, para quien trabaja el caso, pedir apoyo. */
export function SupportTab({ caseId, ownUnitId }: { caseId: string; ownUnitId: string | null }) {
  const q = useCaseSupport(caseId);
  const [requesting, setRequesting] = useState(false);
  const canRequest = !!q.data?.canRequest;
  return (
    <Card title={<span className="inline-flex items-center gap-2"><Icono nombre="mdi:account-multiple-outline" aria-hidden /> Apoyos de otras áreas</span>}
      actions={q.data?.canRequest ? <Button size="sm" disabled={!canRequest} onClick={() => setRequesting(true)}><Icono nombre="mdi:plus" /> Pedir apoyo</Button> : undefined}
      description="Comunicaciones internas entre áreas para elaborar la respuesta. No se envían al ciudadano: la respuesta oficial va aparte, en la tarea «Responder».">
      {q.isLoading ? <LoadingState rows={3} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar las solicitudes de apoyo." /> : !q.data?.requests.length ? (
        <EmptyState title="Este caso no tiene solicitudes de apoyo." hint={q.data?.canRequest ? 'Si la petición incluye temas de otra dependencia, pídele apoyo con «Pedir apoyo».' : undefined} icon="mdi:account-multiple-outline" />
      ) : (
        <div className="flex flex-col gap-4">
          <SupportSummaryBar summary={q.data.summary} />
          <ul className="list-none m-0 p-0 flex flex-col gap-2">{q.data.requests.map((r) => <SupportRequestItem key={r.id} r={r} caseId={caseId} />)}</ul>
        </div>
      )}
      {requesting && <RequestDialog caseId={caseId} ownUnitId={ownUnitId} onHide={() => setRequesting(false)} />}
    </Card>
  );
}

/**
 * Lo que respondieron las áreas, al lado del borrador de la respuesta oficial: insumo para escribirla, nunca parte de
 * ella. En `compact` (tarjeta de la próxima acción) va como sección plegable.
 */
export function SupportForResponse({ caseId, ownUnitId = null, compact = false }: { caseId: string; ownUnitId?: string | null; compact?: boolean }) {
  const q = useCaseSupport(caseId);
  const [requesting, setRequesting] = useState(false);
  const data = q.data;
  const active = data?.requests.filter((r) => r.status !== 'CANCELLED') ?? [];
  // Sin solicitudes y sin poder pedirlas (solo consulta, caso terminado) no hay nada que mostrar.
  if (!data || (!active.length && !data.canRequest)) return null;
  const answered = data.requests.filter((r) => r.status === 'ANSWERED');
  const requestButton = data.canRequest && <Button size="sm" variant="outline" onClick={() => setRequesting(true)}><Icono nombre="mdi:account-multiple-plus-outline" /> Pedir apoyo a otra área</Button>;
  const body = (
    <div className="flex flex-col gap-3">
      {!active.length ? (
        <p className="text-sm text-muted-foreground m-0">Si la petición incluye temas de otra dependencia, pídele la información o el concepto que necesitas. Su respuesta llega aquí como insumo; no se envía al ciudadano.</p>
      ) : (<>
        <SupportSummaryBar summary={data.summary} />
        {data.summary.pending > 0 && <Alert variant="warning"><Icono nombre="mdi:clock-outline" /><AlertTitle>Hay apoyos pendientes</AlertTitle><AlertDescription>Puedes esperar su respuesta o seguir sin ella; si ya no hace falta, cancélala en la pestaña «Apoyos».</AlertDescription></Alert>}
        {answered.length > 0 && <ul className="list-none m-0 p-0 flex flex-col gap-2 max-h-[28rem] overflow-auto">{answered.map((r) => <SupportRequestItem key={r.id} r={r} caseId={caseId} />)}</ul>}
      </>)}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {active.length > 0 ? <Link to={`/pqrs/${caseId}?tab=apoyos`} className="text-xs inline-flex items-center gap-1">Abrir la pestaña de apoyos <Icono nombre="mdi:arrow-right" aria-hidden /></Link> : <span />}
        {requestButton}
      </div>
      {requesting && <RequestDialog caseId={caseId} ownUnitId={ownUnitId} onHide={() => setRequesting(false)} />}
    </div>
  );
  const title = active.length ? `Apoyos de otras áreas (${answered.length} de ${active.length} respondidas)` : 'Apoyos de otras áreas';
  if (compact) return <Plegable titulo={<span className="text-sm font-medium">{title}</span>} defaultAbierto={active.length > 0}>{body}</Plegable>;
  return <Card title={title}>{body}</Card>;
}
